import { useEffect } from "react";
import { useTournament } from "./TournamentContext";
import styles from "./TournamentView.module.css";
import StatusMessage from "../../../components/StatusMessage/StatusMessage.js";

// 총점 → 비교전(슛오프) 점수 순으로 비교
const compareScore = (a, b) =>
  a.total_score - b.total_score || a.special_point - b.special_point;

// 승자 선수 ID: 다음 라운드에 올라간 선수, 다음 라운드가 없으면(결승, 3/4위전) 점수가 높은 선수
const getWinnerId = (match, nextRound) => {
  if (!match) return null;
  // 승자가 정해진 데이터(2025 입력 결과)
  if (match.winner_id) return match.winner_id;

  if (nextRound) {
    const advancedIds = new Set(
      nextRound.flatMap((m) => m.players.map((p) => p.player_id))
    );
    return match.players.find((p) => advancedIds.has(p.player_id))?.player_id ?? null;
  }

  const [a, b] = match.players;
  if (!a || !b) return null;
  const diff = compareScore(a, b);
  if (diff === 0) return null;
  return diff > 0 ? a.player_id : b.player_id;
};

const PLACE_LABELS = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th" };

// 16강 대진 배치: 예선 몇 위 자리끼리 붙는지 (round16 인덱스 순서, 서버 CreateTournamentJoJoin.SEED_16과 같은 순서)
const ROUND16_SEEDS = [
  [1, 16], [8, 9], [5, 12], [4, 13], // 왼쪽 (1위·4위는 4강에서 만남)
  [3, 14], [6, 11], [7, 10], [2, 15], // 오른쪽 (2위·3위는 4강에서 만남, 1·2위는 결승에서)
];

// places: 결승 [1, 2], 3/4위전 [3, 4] → 승자/패자에게 최종 순위 표시 (WIN 대신)
// seeds: 16강 자리 배치 [위 선수, 아래 선수] → 자리 순위 표시
const Players = ({ match, nextRound, places, seeds }) => {
  const winnerId = getWinnerId(match, nextRound);

  // 승자를 위에 표시 (자리 순위도 선수와 함께 이동)
  const slots = [0, 1].map((i) => ({
    player: match?.getPlayer(i),
    seed: seeds?.[i],
  }));
  if (slots[1].player && slots[1].player.player_id === winnerId) slots.reverse();

  return slots.map(({ player, seed }, index) => {
    const isWinner = player && player.player_id === winnerId;
    // 승자가 정해졌을 때만 순위 표시 (승자는 위, 패자는 아래)
    const place = places && winnerId !== null ? places[index] : null;
    return (
      <div
        key={index}
        className={`${styles.player} ${isWinner ? styles.winRow : ""}`}
      >
        {seed && <span className={styles.seed}>{seed}</span>}
        {player?.player_name || "?"}
        {player?.special_point > 0 && (
          <span className={styles.shootOff}>SO {player.special_point}</span>
        )}
        {place ? (
          <span className={`${styles.place} ${styles[`place${place}`]}`}>
            {PLACE_LABELS[place]}
          </span>
        ) : (
          isWinner && <span className={styles.winner}>WIN</span>
        )}
      </div>
    );
  });
};

const TournamentView = ({ gender, distance }) => {
  const { tournament, fetchTournament, loading, error } = useTournament();

  useEffect(() => {
    fetchTournament(gender, distance);
  }, [gender, distance, fetchTournament]);

  if (loading) return <StatusMessage type="loading" />;
  if (error) return <StatusMessage type="error" description={error} />;

  const round16 = tournament?.round16 || [];
  const round8 = tournament?.round8 || [];
  const round4 = tournament?.round4 || [];
  const round3 = tournament?.round3 || [];
  const round2 = tournament?.round2 || [];

  // 본선 전(16강 선수가 아직 없음)이면 빈 대진표 대신 안내
  const hasPlayers = [round16, round8, round4, round3, round2].some((round) =>
    round.some((match) => match?.players?.length > 0)
  );
  if (!hasPlayers) return <StatusMessage />;

  // half: 0 = 왼쪽(16강 0~3), 1 = 오른쪽(16강 4~7)
  const renderRound16 = (half) => (
    <div className={styles.round}>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className={`${styles.match} ${
            i % 2 === 0 ? styles.matchBt : styles.matchTop
          } ${half === 1 ? styles.matchLeft : ""}`}
        >
          <Players
            match={round16[half * 4 + i]}
            nextRound={round8}
            seeds={
              // 2025 입력 결과는 2026 배치와 순서가 달라 자리 순위를 표시하지 않음
              tournament?.fromResults ? undefined : ROUND16_SEEDS[half * 4 + i]
            }
          />
        </div>
      ))}
    </div>
  );

  const renderRound8 = (half) =>
    [0, 1].map((i) => (
      <div key={i} className={styles.roundTitle}>
        <Players match={round8[half * 2 + i]} nextRound={round4} />
      </div>
    ));

  return (
    <div className={styles.bracketScroll}>
      <div className={`bracket ${styles.bracket}`}>
        {/* 왼쪽 16강 (4개 매치) */}
        {renderRound16(0)}

        {/* 왼쪽 8강 (2개 매치) */}
        <div className={styles.round}>
          {renderRound8(0)}
          <span className={`${styles.roundTitleLine} ${styles.lineRight}`}></span>
        </div>

        {/* 왼쪽 4강 (1개 매치) */}
        <div className={styles.round}>
          <div className={`${styles.roundTitleSemi} ${styles.roundTitleSemiLeft}`}>
            <span className={styles.roundLabel}>Semi Final</span>
            <Players match={round4[0]} nextRound={round2} />
          </div>
        </div>

        {/* 결승 & 3/4위전 */}
        <div className={styles.round}>
          <div className={styles.final}>
            <span className={styles.roundLabel}>Final</span>
            <Players match={round2[0]} places={[1, 2]} />
          </div>
          <div className={styles.semiFinal}>
            <span className={styles.roundLabel}>3/4</span>
            <Players match={round3[0]} places={[3, 4]} />
          </div>
        </div>

        {/* 오른쪽 4강 (1개 매치) */}
        <div className={styles.round}>
          <div className={`${styles.roundTitleSemi} ${styles.roundTitleSemiRight}`}>
            <span className={styles.roundLabel}>Semi Final</span>
            <Players match={round4[1]} nextRound={round2} />
          </div>
        </div>

        {/* 오른쪽 8강 (2개 매치) */}
        <div className={styles.round}>
          <span className={`${styles.roundTitleLine} ${styles.lineLeft}`}></span>
          {renderRound8(1)}
        </div>

        {/* 오른쪽 16강 (4개 매치) */}
        {renderRound16(1)}
      </div>
    </div>
  );
};

export default TournamentView;
